// ============================================================
// PropertyPage.jsx - THE consolidated per-property page. One
// property, one page, everything as sub-tabs: Content (address/
// wifi/maps/Hostex mapping), Photos, Amenities, Inventory (internal),
// Access & Codes, Autour, Reglement.
//
// Reuses the already-tested tab components (PhotosTab, ContentTab,
// HouseRulesTab, AutourTab from PropertySettings.jsx; Amenities.jsx
// unchanged) and adds the new CodesTab / InventoryTab / Hostex
// mapping built for this consolidation.
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import Amenities from './Amenities';
import CodesTab from './CodesTab';
import SendCheckinTab from './SendCheckinTab';
import InventoryTab from './InventoryTab';
import HostexMappingCard from './HostexMappingCard';

const IconPhoto = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>;
const IconMap = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 20l-6-3V4l6 3 6-3 6 3v13l-6-3-6 3z"/><path d="M9 4v13M15 7v13"/></svg>;
const IconTool = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14.7 6.3a4 4 0 10-5.4 5.4L2 19l3 3 7.3-7.3a4 4 0 005.4-5.4z"/></svg>;
const IconBox = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 8L12 3 3 8v8l9 5 9-5V8z"/><path d="M3 8l9 5 9-5M12 13v8"/></svg>;
const IconKey = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="15" r="3"/><path d="M10.5 12.5L18 5l2 2-1.5 1.5M15 8l2 2"/></svg>;
const IconRules = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="9"/></svg>;
const IconAutour = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="10" r="3"/><path d="M12 21s7-6.5 7-11a7 7 0 10-14 0c0 4.5 7 11 7 11z"/></svg>;
const IconStar = <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.9 6.6 7.1.6-5.4 4.7 1.7 7-6.3-3.8L5.7 21l1.7-7L2 9.2l7.1-.6z"/></svg>;
const IconTrash = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>;
const IconSmoke = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="9"/><path d="M5 12h14"/></svg>;
const IconMoon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 3a6 6 0 100 18 9 9 0 01-9-9 6 6 0 019-9z"/></svg>;
const IconParty = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="9"/><path d="M5 12h14M12 5v14"/></svg>;
const IconPaw = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 15c0-3 2-4 2-7a2 2 0 114 0c0 2-1 3-1 5m6-5c0-2-1-3-1-5a2 2 0 114 0c0 3 2 4 2 7"/><path d="M8 13c-2 1-3 3-3 5a3 3 0 003 3h8a3 3 0 003-3c0-2-1-4-3-5"/></svg>;
const IconPlus = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 5v14M5 12h14"/></svg>;

const LANGS = [
  { code: 'fr', label: 'FR' }, { code: 'en', label: 'EN' },
  { code: 'es', label: 'ES' }, { code: 'ar', label: 'AR' },
];

function Toggle({ on, onChange }) {
  return <button type="button" className={`toggle-switch ${on ? 'on' : ''}`} onClick={() => onChange(!on)} />;
}
function LangTabs({ activeLang, onChange }) {
  return (
    <div className="lang-tabs">
      {LANGS.map(l => (
        <button key={l.code} className={`lang-tab ${activeLang === l.code ? 'on' : ''}`} onClick={() => onChange(l.code)} type="button">{l.label}</button>
      ))}
    </div>
  );
}

// ---------------- Content tab: address/wifi/maps/Hostex ----------------
function ContentSection({ propertyId, m, toast }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    m(`/admin/properties/${propertyId}/content`).then(r => r.json())
      .then(d => { if (d.success) setContent(d.data); })
      .finally(() => setLoading(false));
  }, [propertyId, m]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    const res = await m(`/admin/properties/${propertyId}/content`, { method: 'PATCH', body: JSON.stringify(patch) });
    const data = await res.json();
    if (data.success) toast('Enregistre', 'ok');
    else toast(data.error || 'Erreur', 'err');
  };

  if (loading || !content) return <div className="empty"><span className="spin" /></div>;

  const hasCoords = content.lat != null && content.lng != null;
  const googleUrl = hasCoords ? `https://www.google.com/maps/search/?api=1&query=${content.lat},${content.lng}` : null;
  const appleUrl = hasCoords ? `https://maps.apple.com/?ll=${content.lat},${content.lng}` : null;

  return (
    <div>
      <div className="ff">
        <label className="fl">Adresse</label>
        <input className="fi" defaultValue={content.address || ''} onBlur={(e) => save({ address: e.target.value })} placeholder="12 Rue des Amandiers, Casablanca" />
      </div>
      <div className="frow">
        <div className="ff">
          <label className="fl">Latitude</label>
          <input className="fi" type="number" step="any" defaultValue={content.lat ?? ''} onBlur={(e) => save({ lat: parseFloat(e.target.value) || null })} placeholder="33.5731" />
        </div>
        <div className="ff">
          <label className="fl">Longitude</label>
          <input className="fi" type="number" step="any" defaultValue={content.lng ?? ''} onBlur={(e) => save({ lng: parseFloat(e.target.value) || null })} placeholder="-7.5898" />
        </div>
      </div>
      <div className="map-links-row">
        <a className={`map-link-chip ${!hasCoords ? 'disabled' : ''}`} href={googleUrl || '#'} target="_blank" rel="noreferrer">{IconMap} Google Maps</a>
        <a className={`map-link-chip ${!hasCoords ? 'disabled' : ''}`} href={appleUrl || '#'} target="_blank" rel="noreferrer">{IconMap} Apple Plans</a>
      </div>

      <hr style={{ border: 'none', borderTop: '0.5px solid var(--border)', margin: '18px 0' }} />

      <div className="frow">
        <div className="ff">
          <label className="fl">Nom du reseau Wifi</label>
          <input className="fi" defaultValue={content.wifi_ssid || ''} onBlur={(e) => save({ wifi_ssid: e.target.value })} placeholder="Oliviers_Guest" />
        </div>
        <div className="ff">
          <label className="fl">Mot de passe Wifi</label>
          <input className="fi" defaultValue={content.wifi_password || ''} onBlur={(e) => save({ wifi_password: e.target.value })} placeholder="olivier2024" />
        </div>
      </div>

      <hr style={{ border: 'none', borderTop: '0.5px solid var(--border)', margin: '18px 0' }} />

      <div className="frow">
        <div className="ff">
          <label className="fl">Nom du manager (hote affiche au guest)</label>
          <input className="fi" defaultValue={content.manager_name || ''} onBlur={(e) => save({ manager_name: e.target.value })} placeholder="Oussama" />
        </div>
        <div className="ff">
          <label className="fl">WhatsApp du manager (format international, sans +)</label>
          <input className="fi" defaultValue={content.manager_whatsapp || ''} onBlur={(e) => save({ manager_whatsapp: e.target.value })} placeholder="212600000000" />
        </div>
      </div>

      <hr style={{ border: 'none', borderTop: '0.5px solid var(--border)', margin: '18px 0' }} />

      <HostexMappingCard propertyId={propertyId} currentValue={content.hostex_property_id} m={m} toast={toast} onSaved={load} />
    </div>
  );
}

// ---------------- Photos tab ----------------
function PhotosSection({ propertyId, m, toast }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [urlInput, setUrlInput] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    m(`/admin/properties/${propertyId}/photos`).then(r => r.json())
      .then(d => { if (d.success) setPhotos(d.data); })
      .finally(() => setLoading(false));
  }, [propertyId, m]);

  useEffect(() => { load(); }, [load]);

  const addPhoto = async () => {
    if (!urlInput.trim()) return;
    const res = await m(`/admin/properties/${propertyId}/photos`, {
      method: 'POST',
      body: JSON.stringify({ url: urlInput.trim(), is_hero: photos.length === 0 }),
    });
    const data = await res.json();
    if (data.success) { setUrlInput(''); toast('Photo ajoutee', 'ok'); load(); }
    else toast(data.error || 'Erreur', 'err');
  };

  const setHero = async (photoId) => {
    const res = await m(`/admin/photos/${photoId}`, { method: 'PATCH', body: JSON.stringify({ is_hero: true }) });
    const data = await res.json();
    if (data.success) { toast('Photo principale mise a jour', 'ok'); load(); }
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    const res = await m(`/admin/photos/${photoId}`, { method: 'DELETE' });
    const data = await res.json();
    if (data.success) { toast('Photo supprimee', 'ok'); load(); }
  };

  return (
    <div>
      <div className="ff">
        <label className="fl">Ajouter une photo (URL)</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="fi" value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="https://..." onKeyDown={(e) => e.key === 'Enter' && addPhoto()} />
          <button className="btn btn-p" onClick={addPhoto} type="button">Ajouter</button>
        </div>
      </div>
      {loading ? <div className="empty"><span className="spin" /></div> : (
        <div className="photo-grid">
          {photos.map(photo => (
            <div key={photo.id} className="photo-card">
              <img src={photo.url} alt="" />
              {!!photo.is_hero && <span className="photo-hero-badge">Hero</span>}
              <div className="photo-actions">
                {!photo.is_hero && (
                  <button className="photo-action-btn" onClick={() => setHero(photo.id)} title="Photo principale" type="button">{IconStar}</button>
                )}
                <button className="photo-action-btn" onClick={() => deletePhoto(photo.id)} title="Supprimer" type="button">{IconTrash}</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------- House rules tab ----------------
function RulesSection({ propertyId, m, toast }) {
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    m(`/admin/properties/${propertyId}/content`).then(r => r.json())
      .then(d => { if (d.success) setRules(d.data.house_rules || { max_occupancy: 4, smoking_allowed: false, quiet_hours_start: '22:00', parties_allowed: false, pets_allowed: false }); })
      .finally(() => setLoading(false));
  }, [propertyId, m]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    const next = { ...rules, ...patch };
    setRules(next);
    const res = await m(`/admin/properties/${propertyId}/house-rules`, { method: 'PUT', body: JSON.stringify(next) });
    const data = await res.json();
    if (data.success) toast('Reglement mis a jour', 'ok');
  };

  if (loading || !rules) return <div className="empty"><span className="spin" /></div>;

  return (
    <div>
      <div className="ff">
        <label className="fl">Nombre maximum d'occupants</label>
        <input className="fi" type="number" min="1" defaultValue={rules.max_occupancy || 4} onBlur={(e) => save({ max_occupancy: parseInt(e.target.value) || 1 })} style={{ maxWidth: 120 }} />
      </div>
      <div className="rule-toggle-row">
        <div className="rule-toggle-label">{IconSmoke} Fumer autorise</div>
        <Toggle on={!!rules.smoking_allowed} onChange={(v) => save({ smoking_allowed: v })} />
      </div>
      <div className="rule-toggle-row">
        <div className="rule-toggle-label">{IconParty} Fetes / evenements autorises</div>
        <Toggle on={!!rules.parties_allowed} onChange={(v) => save({ parties_allowed: v })} />
      </div>
      <div className="rule-toggle-row">
        <div className="rule-toggle-label">{IconPaw} Animaux autorises</div>
        <Toggle on={!!rules.pets_allowed} onChange={(v) => save({ pets_allowed: v })} />
      </div>
      <div className="rule-toggle-row">
        <div className="rule-toggle-label">{IconMoon} Silence a partir de</div>
        <input className="fi" type="time" defaultValue={rules.quiet_hours_start || '22:00'} onBlur={(e) => save({ quiet_hours_start: e.target.value })} style={{ width: 110, padding: '6px 10px' }} />
      </div>
    </div>
  );
}

// ---------------- Autour tab ----------------
function RecCard({ rec, onUpdate, onDelete }) {
  const [lang, setLang] = useState('fr');
  return (
    <div className="rec-card">
      <img className="rec-photo" src={rec.photo_url || 'https://via.placeholder.com/64'} alt="" />
      <div className="rec-body">
        <LangTabs activeLang={lang} onChange={setLang} />
        <div className="rec-fields">
          <div className="ff">
            <input className="fi" defaultValue={rec.name?.[lang] || ''} onBlur={(e) => onUpdate({ name: { ...rec.name, [lang]: e.target.value } })} placeholder={lang === 'fr' ? 'Nom' : `Nom en ${lang.toUpperCase()}`} />
          </div>
          <div className="ff">
            <textarea className="fta" style={{ minHeight: 50 }} defaultValue={rec.description?.[lang] || ''} onBlur={(e) => onUpdate({ description: { ...rec.description, [lang]: e.target.value } })} placeholder="Description courte" />
          </div>
          <div className="rec-meta-row">
            <input className="fi" defaultValue={rec.photo_url || ''} onBlur={(e) => onUpdate({ photo_url: e.target.value })} placeholder="URL photo" />
            <input className="fi" type="number" defaultValue={rec.walk_time_minutes || ''} onBlur={(e) => onUpdate({ walk_time_minutes: parseInt(e.target.value) || null })} placeholder="Min. a pied" style={{ maxWidth: 100 }} />
            <button className="step-icon-btn danger" onClick={onDelete} type="button">{IconTrash}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AutourSection({ propertyId, m, toast }) {
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    m(`/admin/properties/${propertyId}/local-recommendations`).then(r => r.json())
      .then(d => { if (d.success) setRecs(d.data); })
      .finally(() => setLoading(false));
  }, [propertyId, m]);

  useEffect(() => { load(); }, [load]);

  const addRec = async () => {
    const res = await m(`/admin/properties/${propertyId}/local-recommendations`, { method: 'POST', body: JSON.stringify({ name: { fr: 'Nouveau lieu' } }) });
    const data = await res.json();
    if (data.success) load();
  };
  const updateRec = async (recId, patch) => {
    setRecs(prev => prev.map(r => r.id === recId ? { ...r, ...patch } : r));
    await m(`/admin/local-recommendations/${recId}`, { method: 'PATCH', body: JSON.stringify(patch) });
  };
  const deleteRec = async (recId) => {
    if (!window.confirm('Supprimer ?')) return;
    const res = await m(`/admin/local-recommendations/${recId}`, { method: 'DELETE' });
    const data = await res.json();
    if (data.success) { toast('Supprime', 'ok'); load(); }
  };

  return (
    <div>
      {loading ? <div className="empty"><span className="spin" /></div> : (
        <>
          {recs.map(rec => <RecCard key={rec.id} rec={rec} onUpdate={(p) => updateRec(rec.id, p)} onDelete={() => deleteRec(rec.id)} />)}
          <button className="add-step-btn" onClick={addRec} type="button">{IconPlus} Ajouter une recommandation</button>
        </>
      )}
    </div>
  );
}

// ---------------- main consolidated page ----------------
const TABS = [
  { id: 'content', label: 'Contenu', icon: IconMap },
  { id: 'photos', label: 'Photos', icon: IconPhoto },
  { id: 'amenities', label: 'Equipements', icon: IconTool },
  { id: 'inventory', label: 'Inventaire', icon: IconBox },
  { id: 'codes', label: 'Acces & Codes', icon: IconKey },
  { id: 'checkin', label: 'Envoi instructions', icon: IconKey },
  { id: 'rules', label: 'Reglement', icon: IconRules },
  { id: 'autour', label: 'Autour', icon: IconAutour },
];

export default function PropertyPage({ property, m, toast }) {
  const [tab, setTab] = useState('content');

  if (!property) {
    return (
      <div className="empty">
        <div className="empty-t">Selectionnez une propriete</div>
      </div>
    );
  }

  return (
    <div>
      <div className="settings-tabs">
        {TABS.map(t => (
          <button key={t.id} className={`settings-tab ${tab === t.id ? 'on' : ''}`} onClick={() => setTab(t.id)} type="button">
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div className="card" style={{ padding: 18 }}>
        {tab === 'content' && <ContentSection propertyId={property.id} m={m} toast={toast} />}
        {tab === 'photos' && <PhotosSection propertyId={property.id} m={m} toast={toast} />}
        {tab === 'amenities' && <Amenities propertyId={property.id} propertyName={property.name} m={m} toast={toast} />}
        {tab === 'inventory' && <InventoryTab propertyId={property.id} m={m} toast={toast} />}
        {tab === 'codes' && <CodesTab propertyId={property.id} m={m} toast={toast} />}
        {tab === 'checkin' && <SendCheckinTab propertyId={property.id} m={m} toast={toast} />}
        {tab === 'rules' && <RulesSection propertyId={property.id} m={m} toast={toast} />}
        {tab === 'autour' && <AutourSection propertyId={property.id} m={m} toast={toast} />}
      </div>
    </div>
  );
}
